import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  DollarSign,
  Clock,
  AlertCircle,
  TrendingUp,
  Calendar,
  User,
  FolderOpen
} from 'lucide-react'
import DataTable from '../../../components/DataTable/DataTable'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import budgetPlanningService from '../../../services/budgetPlanningService'
import RevisionHistoryModal from '../../../components/RevisionHistoryModal/RevisionHistoryModal'
import '../../../components/RevisionHistoryModal/RevisionHistoryModal.css'
import '../../../components/ConfirmationModal/ConfirmationModal.css'

const BudgetPlanningList = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalBudgets: 0,
    draft: 0,
    approved: 0,
    active: 0,
    closed: 0
  })
  const [showRevisionHistory, setShowRevisionHistory] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [selectedBudgetPlanningData, setSelectedBudgetPlanningData] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [budgetsResponse, statsResponse] = await Promise.all([
        budgetPlanningService.getAllBudgetPlannings(),
        budgetPlanningService.getBudgetPlanningStats()
      ])

      if (budgetsResponse.success !== false && budgetsResponse.budgetPlannings) {
        setData(budgetsResponse.budgetPlannings || [])
        
        // Show toast if no data
        if ((budgetsResponse.budgetPlannings || []).length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No budget plannings found. Create your first budget plan to get started!')
        }
      } else {
        toast.error(budgetsResponse.message || 'Failed to load budget plannings')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data || {})
      }
    } catch (error) {
      console.error('Error loading data:', error)
      if (!error._errorHandled) {
        toast.error('Failed to load budget plannings data')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleAddNew = () => {
    navigate('/projects/budget-plannings/new')
  }

  const handleEdit = (budget) => {
    const budgetId = budget._id || budget.id
    if (!budgetId) {
      toast.error('Budget planning ID not found')
      return
    }
    navigate(`/projects/budget-plannings/edit/${budgetId}`)
  }

  const handleView = (budget) => {
    const budgetId = budget._id || budget.id
    if (!budgetId) {
      toast.error('Budget planning ID not found')
      return
    }
    navigate(`/projects/budget-plannings/view/${budgetId}`)
  }

  const handleDelete = (budget) => {
    setDeleteItem(budget)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const budgetId = deleteItem._id || deleteItem.id
      if (!budgetId) {
        toast.error('Budget planning ID not found')
        return
      }
      const response = await budgetPlanningService.deleteBudgetPlanning(budgetId)
      if (response.success) {
        toast.success('Budget planning deleted successfully')
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete budget planning')
      }
    } catch (error) {
      console.error('Error deleting budget planning:', error)
      if (!error._errorHandled) {
        toast.error('Failed to delete budget planning')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const handleRevisionHistory = (budget) => {
    if (!budget) {
      toast.error('Budget planning data not found')
      return
    }
    setSelectedBudgetPlanningData(budget)
    setShowRevisionHistory(true)
  }

  const handleRowSelection = (selectedRows) => {
    setSelectedRows(selectedRows)
  }

  const handleSelectionChange = (selectedRows) => {
    setSelectedRows(selectedRows)
  }

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const day = String(date.getDate()).padStart(2, '0')
    const month = months[date.getMonth()]
    const year = date.getFullYear()
    return `${day}-${month}-${year}`
  }

  // Format currency helper
  const formatCurrency = (amount, currency = 'INR') => {
    if (!amount && amount !== 0) return 'N/A'
    const formatter = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })
    return formatter.format(amount)
  }

  // Define columns for the data table
  const columns = [
    {
      key: 'budgetNumber',
      label: 'Budget #',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-900">
          {value}
        </div>
      )
    },
    {
      key: 'budgetName',
      label: 'Budget Name',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-900">
          {value}
        </div>
      )
    },
    {
      key: 'project',
      label: 'Project',
      sortable: true,
      render: (value, row) => {
        const project = row.project
        if (!project) return <span className="text-gray-500">N/A</span>
        const projectName = typeof project === 'string' 
          ? project 
          : (project.projectName || project.name || 'N/A')
        return (
          <div className="font-medium text-gray-900">
            {projectName}
          </div>
        )
      }
    },
    {
      key: 'budgetOwner',
      label: 'Budget Owner',
      sortable: true,
      render: (value, row) => {
        const owner = row.budgetOwner
        if (!owner) return <span className="text-gray-500">N/A</span>
        const ownerName = typeof owner === 'string' 
          ? owner 
          : (owner.employeeName || owner.name || 'N/A')
        return (
          <div className="font-medium text-gray-900">
            {ownerName}
          </div>
        )
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusColors = {
          'Draft': '#6B7280',
          'Submitted': '#3B82F6',
          'Under Review': '#F59E0B',
          'Approved': '#10B981',
          'Active': '#10B981',
          'Closed': '#6B7280',
          'Cancelled': '#EF4444'
        }
        const statusColor = statusColors[value] || '#6B7280'
        return (
          <span 
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ 
              backgroundColor: `${statusColor}20`,
              color: statusColor,
              border: `1px solid ${statusColor}40`
            }}
          >
            {value || 'N/A'}
          </span>
        )
      }
    },
    {
      key: 'totalBudgeted',
      label: 'Budgeted Amount',
      sortable: true,
      render: (value, row) => (
        <div className="font-medium text-gray-900">
          {formatCurrency(value, row.currency || 'INR')}
        </div>
      )
    },
    {
      key: 'totalActual',
      label: 'Actual Amount',
      sortable: true,
      render: (value, row) => (
        <div className="font-medium text-gray-900">
          {formatCurrency(value, row.currency || 'INR')}
        </div>
      )
    },
    {
      key: 'budgetUtilization',
      label: 'Utilization',
      sortable: true,
      render: (value, row) => {
        const utilization = row.budgetUtilization || 0
        const color = utilization >= 95 ? '#EF4444' : utilization >= 80 ? '#F59E0B' : '#10B981'
        return (
          <div className="flex items-center gap-2">
            <div className="w-16 bg-gray-200 rounded-full h-2">
              <div 
                className="h-2 rounded-full" 
                style={{ width: `${Math.min(100, utilization)}%`, backgroundColor: color }}
              ></div>
            </div>
            <span className="text-sm text-gray-600">{utilization || 0}%</span>
          </div>
        )
      }
    }
  ]

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Transactions', path: '/projects?tab=transactions', onClick: () => navigate('/projects?tab=transactions') },
    { label: 'Budget Planning List', path: '/projects/budget-plannings/list', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Budget Planning List</h1>
          <p>Create, view, and manage all budget plans</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-primary"
            onClick={handleAddNew}
          >
            <Plus size={16} />
            Add New Budget Plan
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalBudgets || 0}</div>
          <div className="metric-title">Total Budgets</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <TrendingUp size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.active || 0}</div>
          <div className="metric-title">Active</div>
          <div className="metric-subtitle">Currently active</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+5%</span>
            </div>
          </div>
          <div className="metric-value">{stats.approved || 0}</div>
          <div className="metric-title">Approved</div>
          <div className="metric-subtitle">Approved budgets</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <AlertCircle size={16} />
            </div>
            <div className="metric-trend negative">
              <TrendingUp size={12} />
              <span>+3%</span>
            </div>
          </div>
          <div className="metric-value">{stats.underReview || 0}</div>
          <div className="metric-title">Under Review</div>
          <div className="metric-subtitle">Pending approval</div>
        </div>
      </div>

      {/* Budget Planning List Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onRevisionHistory={handleRevisionHistory}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search budget plannings..."
          emptyMessage="No budget plannings found. Create your first budget plan to get started!"
          noCard={true}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Budget Planning"
        message={`Are you sure you want to delete "${deleteItem?.budgetName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Revision History Modal */}
      <RevisionHistoryModal
        isOpen={showRevisionHistory}
        onClose={() => setShowRevisionHistory(false)}
        data={selectedBudgetPlanningData}
        title="Budget Planning Revision History"
      />
    </div>
  )
}

export default BudgetPlanningList

